import { Box, Button, InputAdornment } from '@mui/material'
import Header from 'components/Header'
import { useAPI } from 'contexts/APIProvider'
import React from 'react'
import { BonusJobStatistics, BonusJobStatisticsSearchForm } from 'types/interfaces'
import { 
  DataGridPro, 
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import ReportSelectBox from 'pages/AdminReports/ReportSelectBox'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import update from 'immutability-helper'
import FormField from 'components/FormField'

const renderPct = (value: number, total: number) => {
  const pct = value / total * 100
  return `${value} (${pct.toFixed(2)}%)`
}

const BonusJobsReport: React.FC = () => {
  const { api } = useAPI()
  const { showAlert } = useSnackBarAlert()
  const [form, setForm] = React.useState<BonusJobStatisticsSearchForm>({
    threshold_for_less_than_12_hours: 1,
    threshold_for_12_to_24_hours: 2,
    threshold_for_24_to_48_hours: 5,
    threshold_for_over_48_hours: 24,
  })
  const [items, setItems] = React.useState<BonusJobStatistics[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)

  const updateForm = React.useCallback((name: string, value: any) => {
    setForm(update(form, { [name]: { $set: value } }))
  }, [form])

  const fetchReport = React.useCallback(async () => {
    try {
      setLoading(true)
      setItems(await api.listBonusJobStatistics(form))
    } catch (e) {
      console.error(e)
      showAlert('error', 'Error loading report')
    } finally {
      setLoading(false)
    }
  }, [api, form, showAlert])

  React.useEffect(() => {
    fetchReport()
  }, [])

  const columns: GridColDef<BonusJobStatistics>[] = React.useMemo(() => [{
    minWidth: 200,
    headerName: 'Practice Group Name',
    field: 'practice_group_name',
    type: 'string',
  }, {
    minWidth: 100,
    headerName: 'Total Jobs',
    field: 'total_jobs',
    type: 'number',
  }, {
    minWidth: 250,
    headerName: 'Bonus Job (< 12 hours)',
    field: 'bonus_job_starting_less_than_12_hours',
    type: 'number',
    renderCell: (params: GridRenderCellParams<BonusJobStatistics>) => {
      const item = params.row
      return renderPct(item.bonus_job_starting_less_than_12_hours, item.total_jobs)
    }
  }, {
    minWidth: 250,
    headerName: 'Bonus Job (12 to 24 hours)',
    field: 'bonus_job_starting_in_12_to_24_hours',
    type: 'number',
    renderCell: (params: GridRenderCellParams<BonusJobStatistics>) => {
      const item = params.row
      return renderPct(item.bonus_job_starting_in_12_to_24_hours, item.total_jobs)
    }
  }, {
    minWidth: 250,
    headerName: 'Bonus Job (24 to 48 hours)',
    field: 'bonus_job_starting_in_24_to_48_hours',
    type: 'number',
    renderCell: (params: GridRenderCellParams<BonusJobStatistics>) => {
      const item = params.row
      return renderPct(item.bonus_job_starting_in_24_to_48_hours, item.total_jobs)
    }
  }, {
    minWidth: 250,
    headerName: 'Bonus Job (48+ hours)',
    field: 'bonus_job_starting_in_over_48_hours',
    type: 'number',
    renderCell: (params: GridRenderCellParams<BonusJobStatistics>) => {
      const item = params.row
      return renderPct(item.bonus_job_starting_in_over_48_hours, item.total_jobs)
    }
  }, {
    minWidth: 250,
    headerName: 'Not Bonus Job',
    field: 'not_bonus_job',
    type: 'number',
    renderCell: (params: GridRenderCellParams<BonusJobStatistics>) => {
      const item = params.row
      return renderPct(item.not_bonus_job, item.total_jobs)
    }
  }], [])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
      <Header text='Bonus Jobs Report'>
        <ReportSelectBox />
      </Header>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
        <FormField
          type='number'
          name='threshold_for_less_than_12_hours'
          label='Threshold for less than 12 hours'
          onChange={(e) => updateForm('threshold_for_less_than_12_hours', e.target.value)}
          value={form.threshold_for_less_than_12_hours ?? ''}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                hours
              </InputAdornment>
            ),
          }}
        />
        <FormField
          type='number'
          name='threshold_for_12_to_24_hours'
          label='Threshold for 12 to 24 hours'
          value={form.threshold_for_12_to_24_hours ?? ''}
          onChange={(e) => updateForm('threshold_for_12_to_24_hours', e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                hours
              </InputAdornment>
            ),
          }}
        />
        <FormField
          type='number'
          name='threshold_for_24_to_48_hours'
          label='Threshold for 24 to 48 hours'
          value={form.threshold_for_24_to_48_hours ?? ''}
          onChange={(e) => updateForm('threshold_for_24_to_48_hours', e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                hours
              </InputAdornment>
            ),
          }}
        />
        <FormField
          type='number'
          name='threshold_for_over_48_hours'
          label='Threshold for over 48 hours'
          onChange={(e) => updateForm('threshold_for_over_48_hours', e.target.value)}
          value={form.threshold_for_over_48_hours ?? ''}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                hours
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          onClick={fetchReport}
          disabled={loading}>
          {loading ? 'Please Wait ...' : 'Search'}
        </Button>
      </Box>
      <DataGridPro 
        rows={items}
        columns={columns}
        getRowId={(item) => item.practice_group_name}
        hideFooter
      />
    </Box>
  )
}

export default BonusJobsReport